import { Link } from "react-router-dom";
import { Building2, Stethoscope, Users2, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const PERSONAS = [
  {
    icon: Building2,
    title: "Dental Labs",
    desc: "Digital-first labs moving on from paper Rx and email threads.",
    to: "/solutions/dental-labs",
  },
  {
    icon: Stethoscope,
    title: "Dental Offices",
    desc: "Doctors who submit cases with full context, right the first time.",
    to: "/solutions/dental-offices",
  },
  {
    icon: Users2,
    title: "Multi-Location Access",
    desc: "Work across every location from one account",
    to: "/solutions/multi-location-access",
  },
];

export default function PersonasGrid() {
  return (
    <section data-testid="personas-grid" className="relative py-16 sm:py-20 bg-secondary/30 dark:bg-secondary/20 border-y hairline">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
            Who uses Rxn3D
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
            Build for everyone in the dental workflow
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PERSONAS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                data-testid={`persona-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
              >
                <Link
                  to={p.to}
                  className="group rounded-2xl hairline p-5 relative overflow-hidden bg-background block h-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-10 w-10 rounded-xl inline-flex items-center justify-center hairline">
                      <Icon className="h-4 w-4 text-brand-plum" />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                      P·{i + 1}
                    </div>
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                  <div className="mt-6 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Explore</span>
                    <span className="text-brand-gradient">→</span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center sm:justify-start">
          <Link
            to="/solutions"
            data-testid="personas-see-all-solutions"
            className="group inline-flex items-center gap-1.5 text-sm font-medium hairline rounded-full px-4 py-2 hover:bg-secondary transition-colors"
          >
            See all solutions
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
